import { useTranslation } from 'react-i18next';
import type { OrganisationMember, OrganisationRole } from '@ghostscreen/contracts';
import { QueryEmptyState } from '../../components/dashboard/QueryEmptyState';
import { QueryErrorState } from '../../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../../components/dashboard/QueryLoadingBlock';
import { formatPersonName, personInitials } from '../../lib/people/personName';
import {
  formatAdminRole,
  isOwnerRole,
  matchesPeopleFilter,
  type PeopleRoleFilter,
} from './teamFormat';

const FILTERS = [
  { id: 'all', labelKey: 'teams:people.filterAll' },
  { id: 'owner', labelKey: 'common:roles.organisation_owner' },
  { id: 'admin', labelKey: 'common:roles.organisation_admin' },
  { id: 'editor', labelKey: 'common:roles.content_manager' },
  { id: 'viewer', labelKey: 'common:roles.viewer' },
] as const satisfies readonly { id: PeopleRoleFilter; labelKey: string }[];

type TeamsPeopleCardProps = {
  members: OrganisationMember[] | undefined;
  teamNamesByUserId: Map<string, string[]>;
  filter: PeopleRoleFilter;
  onFilterChange: (filter: PeopleRoleFilter) => void;
  isPending: boolean;
  isError: boolean;
  isRetrying: boolean;
  onRetry: () => void;
};

export function TeamsPeopleCard({
  members,
  teamNamesByUserId,
  filter,
  onFilterChange,
  isPending,
  isError,
  isRetrying,
  onRetry,
}: TeamsPeopleCardProps) {
  const { t } = useTranslation(['teams', 'common']);
  const filtered =
    members?.filter((member) => matchesPeopleFilter(member.role, filter)) ?? [];
  const total = members?.length ?? 0;

  return (
    <section
      className="rounded-[14px] border border-line px-5 py-5"
      style={{ background: 'var(--gs-panel)' }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0 text-base font-bold text-ink">{t('people.title')}</h2>
          <p className="m-0 mt-0.5 text-xs text-muted">
            {t('people.subtitle', { count: total })}
          </p>
        </div>
        <span
          className="rounded-full px-2.5 py-1 text-[11px] font-semibold text-ink"
          style={{ background: 'var(--gs-field)' }}
        >
          {t('people.badge', { count: total })}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {FILTERS.map((item) => {
          const active = filter === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onFilterChange(item.id)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                active
                  ? 'border-[var(--gs-accent)] text-ink'
                  : 'border-line text-muted hover:text-ink'
              }`}
              style={active ? { background: 'color-mix(in srgb, var(--gs-accent) 12%, transparent)' } : undefined}
            >
              {t(item.labelKey)}
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        {isError || isRetrying ? (
          <QueryErrorState
            message={t('people.loadError')}
            onRetry={onRetry}
            isRetrying={isRetrying}
          />
        ) : isPending ? (
          <QueryLoadingBlock />
        ) : filtered.length === 0 ? (
          <QueryEmptyState>
            {total === 0 ? t('people.emptyOrganisation') : t('people.emptyFilter')}
          </QueryEmptyState>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {filtered.map((member) => {
              const teams = teamNamesByUserId.get(member.userId) ?? [];
              const owner = isOwnerRole(member.role);
              return (
                <li
                  key={member.userId}
                  className="flex items-center gap-3 rounded-[12px] border border-line px-3 py-2.5"
                  style={{ background: 'var(--gs-panel2)' }}
                >
                  <span
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-xs font-bold text-ink"
                    style={{ background: 'var(--gs-field)' }}
                    aria-hidden
                  >
                    {personInitials(member)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">
                      {formatPersonName(member, member.email)}
                    </span>
                    <span className="block truncate text-xs text-muted">{member.email}</span>
                  </span>
                  <span className="hidden min-w-0 max-w-[9rem] truncate text-xs text-muted sm:block">
                    {teams.length === 0 ? t('people.noTeams') : teams.join(' · ')}
                  </span>
                  <span
                    className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                      owner
                        ? 'border-[var(--gs-accent)] text-[var(--gs-accent)]'
                        : 'border-line text-muted'
                    }`}
                  >
                    {formatAdminRole(member.role as OrganisationRole)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
